import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../../../components/layout/Layout';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Spinner } from '../../../components/ui/Spinner';
import { createEmailTemplate } from '../../../modules/comms/api/comms.gateway';
import { showErrorToast, showSuccessToast } from '../../../utils/errorHandler';
import {
  blocksToHtml,
  extractPlaceholders,
  TEMPLATE_REGISTRY,
  TEMPLATE_KEYS,
  type TemplateBlock,
  type StarterBlock,
} from './emailTemplateBuilderUtils';
import { Plus, Trash2 } from 'lucide-react';

function generateId() {
  return `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const hydrateStarterBlocks = (starter: StarterBlock[]): TemplateBlock[] =>
  starter.map((b) => ({ ...b, id: generateId() })) as TemplateBlock[];

const defaultBlock = (type: TemplateBlock['type']): TemplateBlock => {
  const id = generateId();
  switch (type) {
    case 'paragraph':
      return { id, type: 'paragraph', text: '' };
    case 'list':
      return { id, type: 'list', title: '', items: [''] };
    case 'button':
      return { id, type: 'button', label: 'Accept Invitation', href: '{{ invite_link }}' };
    case 'kv':
      return { id, type: 'kv', rows: [{ label: '', value: '' }] };
    case 'divider':
      return { id, type: 'divider' };
    default:
      return { id, type: 'paragraph', text: '' };
  }
};

export const EmailTemplateBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = location.pathname.replace(/\/new$/, '') || '/admin/comms/email-templates';

  const [templateKey, setTemplateKey] = useState<string>(TEMPLATE_KEYS[0] || 'invite_teacher');
  const [name, setName] = useState('');
  const [subjectTemplate, setSubjectTemplate] = useState('');
  const [signature, setSignature] = useState('Best regards,<br/>{{ inviter_name }}');
  const [defaultVariablesJson, setDefaultVariablesJson] = useState('{ "product_name": "Tamil Katral Maiyam" }');
  const [isActive, setIsActive] = useState(true);
  const [blocks, setBlocks] = useState<TemplateBlock[]>([defaultBlock('paragraph')]);
  const [focusedField, setFocusedField] = useState<'subject' | 'signature' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const registry = TEMPLATE_REGISTRY[templateKey];
  const allowedPlaceholders = registry?.allowed ?? [];
  const requiredPlaceholders = registry?.required ?? [];
  const sampleData = registry?.sample ?? {};

  const loadStarter = useCallback(() => {
    const starter = registry?.starter;
    if (!starter) return;
    setName(starter.name);
    setSubjectTemplate(starter.subject_template);
    setSignature(starter.signature);
    setDefaultVariablesJson(JSON.stringify(starter.default_variables ?? {}, null, 2));
    setIsActive(true);
    setBlocks(hydrateStarterBlocks(starter.blocks?.length ? starter.blocks : [{ type: 'paragraph', text: '' }]));
  }, [registry]);

  const resetBuilder = useCallback(() => {
    setName('');
    setSubjectTemplate('');
    setSignature('Best regards,<br/>{{ inviter_name }}');
    setDefaultVariablesJson('{ "product_name": "Tamil Katral Maiyam" }');
    setIsActive(true);
    setBlocks([defaultBlock('paragraph')]);
    setSubmitError(null);
  }, []);

  const bodyHtml = useMemo(
    () => blocksToHtml(blocks, signature),
    [blocks, signature]
  );
  const placeholdersUsed = useMemo(() => {
    const combined = subjectTemplate + bodyHtml + signature;
    return extractPlaceholders(combined);
  }, [subjectTemplate, bodyHtml, signature]);

  const unknownPlaceholders = useMemo(
    () => placeholdersUsed.filter((p) => !allowedPlaceholders.includes(p)),
    [placeholdersUsed, allowedPlaceholders]
  );
  const missingRequired = useMemo(
    () => requiredPlaceholders.filter((p) => !placeholdersUsed.includes(p)),
    [requiredPlaceholders, placeholdersUsed]
  );

  const previewSubject = useMemo(() => {
    let s = subjectTemplate;
    Object.entries(sampleData).forEach(([key, value]) => {
      s = s.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), value);
    });
    return s;
  }, [subjectTemplate, sampleData]);

  const previewBody = useMemo(() => {
    let html = bodyHtml;
    Object.entries(sampleData).forEach(([key, value]) => {
      html = html.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), value);
    });
    return html;
  }, [bodyHtml, sampleData]);

  const insertPlaceholder = useCallback(
    (placeholder: string) => {
      const token = ` {{ ${placeholder} }}`;
      if (focusedField === 'subject') setSubjectTemplate((s) => s + token);
      if (focusedField === 'signature') setSignature((s) => s + token);
    },
    [focusedField]
  );

  const addBlock = useCallback((type: TemplateBlock['type']) => {
    setBlocks((prev) => [...prev, defaultBlock(type)]);
  }, []);

  const removeBlock = useCallback((id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const updateBlock = useCallback((id: string, updater: (b: TemplateBlock) => TemplateBlock) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? updater(b) : b)));
  }, []);

  const parseDefaultVariables = (): Record<string, string> => {
    const trimmed = defaultVariablesJson.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      throw new Error('Invalid JSON in default variables');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (unknownPlaceholders.length > 0) {
      setSubmitError(`Unknown placeholders: ${unknownPlaceholders.join(', ')}`);
      return;
    }
    if (missingRequired.length > 0) {
      setSubmitError(`Missing required placeholders: ${missingRequired.join(', ')}`);
      return;
    }
    let defaultVariables: Record<string, string>;
    try {
      defaultVariables = parseDefaultVariables();
    } catch (err) {
      setSubmitError('Invalid JSON in default variables');
      return;
    }

    setSubmitting(true);
    try {
      await createEmailTemplate({
        template_key: templateKey,
        tenant_id: null,
        name: name || templateKey,
        subject_template: subjectTemplate,
        body_template: bodyHtml,
        signature,
        default_variables: defaultVariables,
        placeholders: placeholdersUsed,
        is_active: isActive,
        channel: 'EMAIL',
      });
      showSuccessToast('Email template created');
      navigate(listPath);
    } catch (err) {
      showErrorToast(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Create Email Template"
        subtitle="Build template with blocks and placeholders"
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column */}
          <div className="space-y-6">
            <Card>
              <CardContent padding="md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-app-text dark:text-white">Template details</h3>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={loadStarter}
                      title="Loads a ready-to-edit starter template for the selected key"
                    >
                      Load Starter
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={resetBuilder} title="Clear form and blocks">
                      Reset
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-app-text-muted dark:text-gray-400 mb-3">
                  Load Starter will overwrite current edits on this page.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">Template key</label>
                    <select
                      value={templateKey}
                      onChange={(e) => setTemplateKey(e.target.value)}
                      className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-3 py-2 text-app-text dark:text-white"
                    >
                      {TEMPLATE_KEYS.map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Invite Teacher (Tenant Default)"
                  />
                  <div onFocus={() => setFocusedField('subject')} onBlur={() => setFocusedField(null)}>
                    <Input
                      label="Subject template"
                      value={subjectTemplate}
                      onChange={(e) => setSubjectTemplate(e.target.value)}
                      placeholder="Action Required: Join {{ product_name }}..."
                    />
                  </div>
                  <div onFocus={() => setFocusedField('signature')} onBlur={() => setFocusedField(null)}>
                    <Textarea
                      label="Signature"
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      rows={3}
                      placeholder="Best regards,<br/>{{ inviter_name }}"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">Default variables (JSON)</label>
                    <textarea
                      value={defaultVariablesJson}
                      onChange={(e) => setDefaultVariablesJson(e.target.value)}
                      className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-3 py-2 text-app-text dark:text-white font-mono text-sm min-h-[80px]"
                      rows={3}
                      spellCheck={false}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="rounded border-app-border dark:border-gray-600 text-primary-600"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-app-text dark:text-gray-300">Active</label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Placeholder chips */}
            <Card>
              <CardContent padding="md">
                <h3 className="text-sm font-semibold text-app-text dark:text-white mb-2">Placeholders</h3>
                <p className="text-xs text-app-text-muted dark:text-gray-400 mb-2">Focus subject or signature, then click to insert.</p>
                <div className="flex flex-wrap gap-2">
                  {allowedPlaceholders.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => insertPlaceholder(p)}
                      className="px-2 py-1 rounded bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs font-medium hover:bg-primary-200 dark:hover:bg-primary-800/60"
                    >
                      {`{{ ${p} }}`}
                    </button>
                  ))}
                </div>
                {unknownPlaceholders.length > 0 && (
                  <p className="mt-2 text-xs text-error-600 dark:text-error-400">Unknown: {unknownPlaceholders.join(', ')}</p>
                )}
                {missingRequired.length > 0 && (
                  <p className="mt-1 text-xs text-warning-600 dark:text-amber-400">Missing required: {missingRequired.join(', ')}</p>
                )}
              </CardContent>
            </Card>

            {/* Block editor */}
            <Card>
              <CardContent padding="md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-app-text dark:text-white">Body blocks</h3>
                  <div className="flex flex-wrap gap-1">
                    {(['paragraph', 'list', 'button', 'kv', 'divider'] as const).map((type) => (
                      <Button key={type} type="button" variant="outline" size="sm" onClick={() => addBlock(type)}>
                        <Plus className="w-3 h-3 mr-1" />
                        {type}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  {blocks.map((block) => (
                    <div
                      key={block.id}
                      className="rounded-lg border border-app-border dark:border-gray-600 bg-app-surface-muted/50 dark:bg-gray-800/50 p-3"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-app-text-muted dark:text-gray-400 uppercase">{block.type}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeBlock(block.id)} aria-label="Remove block">
                          <Trash2 className="w-4 h-4 text-error-500" />
                        </Button>
                      </div>
                      {block.type === 'paragraph' && (
                        <textarea
                          value={block.text}
                          onChange={(e) => updateBlock(block.id, (b) => b.type === 'paragraph' ? { ...b, text: e.target.value } : b)}
                          className="w-full rounded border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-2 py-1.5 text-sm min-h-[60px]"
                          placeholder="Paragraph text..."
                        />
                      )}
                      {block.type === 'list' && (
                        <div className="space-y-2">
                          <input
                            value={block.title ?? ''}
                            onChange={(e) => updateBlock(block.id, (b) => b.type === 'list' ? { ...b, title: e.target.value } : b)}
                            className="w-full rounded border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-2 py-1.5 text-sm"
                            placeholder="List title (optional)"
                          />
                          {(block.items || ['']).map((item, i) => (
                            <input
                              key={i}
                              value={item}
                              onChange={(e) => {
                                const next = [...(block.items || [''])];
                                next[i] = e.target.value;
                                updateBlock(block.id, (b) => b.type === 'list' ? { ...b, items: next } : b);
                              }}
                              className="w-full rounded border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-2 py-1.5 text-sm"
                              placeholder={`Item ${i + 1}`}
                            />
                          ))}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              updateBlock(block.id, (b) =>
                                b.type === 'list' ? { ...b, items: [...(b.items || []), ''] } : b
                              )
                            }
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add item
                          </Button>
                        </div>
                      )}
                      {block.type === 'button' && (
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            value={block.label}
                            onChange={(e) => updateBlock(block.id, (b) => b.type === 'button' ? { ...b, label: e.target.value } : b)}
                            className="rounded border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-2 py-1.5 text-sm"
                            placeholder="Button label"
                          />
                          <input
                            value={block.href}
                            onChange={(e) => updateBlock(block.id, (b) => b.type === 'button' ? { ...b, href: e.target.value } : b)}
                            className="rounded border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-2 py-1.5 text-sm"
                            placeholder="href (e.g. {{ invite_link }})"
                          />
                        </div>
                      )}
                      {block.type === 'kv' && (
                        <div className="space-y-2">
                          {(block.rows || []).map((row, i) => (
                            <div key={i} className="grid grid-cols-2 gap-2">
                              <input
                                value={row.label}
                                onChange={(e) => {
                                  const next = [...(block.rows || [])];
                                  next[i] = { ...next[i], label: e.target.value };
                                  updateBlock(block.id, (b) => b.type === 'kv' ? { ...b, rows: next } : b);
                                }}
                                className="rounded border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-2 py-1.5 text-sm"
                                placeholder="Label"
                              />
                              <input
                                value={row.value}
                                onChange={(e) => {
                                  const next = [...(block.rows || [])];
                                  next[i] = { ...next[i], value: e.target.value };
                                  updateBlock(block.id, (b) => b.type === 'kv' ? { ...b, rows: next } : b);
                                }}
                                className="rounded border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-2 py-1.5 text-sm"
                                placeholder="Value"
                              />
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              updateBlock(block.id, (b) =>
                                b.type === 'kv' ? { ...b, rows: [...(b.rows || []), { label: '', value: '' }] } : b
                              )
                            }
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add row
                          </Button>
                        </div>
                      )}
                      {block.type === 'divider' && <p className="text-xs text-app-text-muted dark:text-gray-400">Horizontal divider</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {submitError && (
              <p className="text-sm text-error-600 dark:text-error-400">{submitError}</p>
            )}
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(listPath)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Spinner size="sm" className="mr-2" /> : null}
                Create Template
              </Button>
            </div>
          </div>

          {/* Right column: preview */}
          <div className="lg:sticky lg:top-4 space-y-4">
            <Card>
              <CardContent padding="md">
                <h3 className="text-sm font-semibold text-app-text dark:text-white mb-2">Preview — Subject</h3>
                <p className="text-app-text dark:text-gray-200 break-words">{previewSubject || '(empty)'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent padding="md">
                <h3 className="text-sm font-semibold text-app-text dark:text-white mb-2">Preview — Body</h3>
                <div
                  className="prose prose-sm max-w-none dark:prose-invert text-app-text dark:text-gray-200 rounded border border-app-border dark:border-gray-700 p-4 bg-white dark:bg-gray-900 min-h-[200px]"
                  dangerouslySetInnerHTML={{ __html: previewBody || '<p class="text-gray-400">(empty)</p>' }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </Layout>
  );
};
